import React, { Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { HERO } from "../../data/content";

const HeroCanvas = lazy(() => import("./HeroCanvas"));

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = ({ onBookCall, onExplore }) => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero">
      <div className="pointer-events-none absolute inset-0 hero-lighting" />
      <div className="pointer-events-none absolute inset-0 grid-texture opacity-70" />

      {/* 3D graph — right/background, non-blocking */}
      <div className="absolute inset-0 md:left-[40%]">
        <Suspense fallback={null}>
          <HeroCanvas />
        </Suspense>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-void via-void/40 to-transparent md:from-void/80" />
      </div>

      <div className="relative container-x w-full pt-28 pb-20">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-8 bg-brand/60" />
            <span className="font-mono text-xs md:text-sm uppercase tracking-[0.22em] text-brand">
              {HERO.overline}
            </span>
          </motion.div>

          <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-light tracking-tighter leading-[1.02]">
            {HERO.headlineLines.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  className="block text-white"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-muted-foreground"
          >
            {HERO.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
          >
            <button
              onClick={onBookCall}
              data-testid="hero-book-call"
              className="group inline-flex items-center justify-center gap-2 rounded-xl bg-brand hover:bg-brand-hover text-white font-medium px-6 py-4 transition-colors duration-200 active:scale-[0.98]"
            >
              {HERO.primaryCta}
              <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              onClick={onExplore}
              data-testid="hero-explore"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium px-6 py-4 transition-colors duration-200 active:scale-[0.98]"
            >
              {HERO.secondaryCta}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-16 grid grid-cols-3 gap-6 max-w-xl border-t border-white/5 pt-8"
          >
            {HERO.stats.map((s) => (
              <div key={s.label} data-testid="hero-stat">
                <div className="font-heading text-xl md:text-2xl text-white">{s.value}</div>
                <div className="mt-1 text-xs md:text-sm text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-muted-foreground"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.div>
    </section>
  );
};

export default Hero;
